import { Fragment, useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetHotelNights } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useOperationsKpiFilter from './useOperationsKpiFilter';
import useExpandedRows from './useExpandedRows';
import localityLabel from './locality';
import {
  ControlFigures,
  nf,
  pct,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from '../management/shared';

const per100 = (value: number | null | undefined) =>
  value === null || value === undefined ? '–' : nf(value, 1);

/**
 * Hotellnätter per 100 inköp/hemleveranser, per kundens län — var hotellnätterna är vanligast.
 * En natt = ett hotellkvitto per fälttestare och dag, se OperationsKpiLogic.GetHotelNights.
 */
const HotelNightsWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } =
    useOperationsKpiFilter();
  const getData = useKPIGetHotelNights();
  const expanded = useExpandedRows();
  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;
  const counties = result?.byCounty ?? [];

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.HotelNights]}
      count={undefined}
      isLoading={getData.isLoading}
      info="Hotellnätter (hotellkvitton, en per fälttestare och dag) per 100 utförda inköp och hemleveranser, per kundens län: från-länet på inköp, till-länet på hemleverans. Nätter på andra ärendetyper visas som jämförelse och ingår inte i kvoten. Klicka på ett län för att dela upp på om fälttestaren har hemankare i länet: ärendet hör till FT på kundbenet, natten till FT som tog hotellet."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={
          !result ||
          (result.totalNights === 0 &&
            result.purchaseAndHomeDeliveryCases === 0)
        }
      >
        <p style={{ fontSize: '15px', margin: '4px 0 12px' }}>
          <strong>{per100(result?.nightsPer100Cases)}</strong> hotellnätter per
          100 inköp/hemleveranser · {nf(result?.totalNights ?? 0)} nätter
          totalt, {nf(result?.averageNightsPerDay ?? 0, 1)} per dygn
        </p>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Kundens län</th>
              <th style={thNumStyle}>Inköp + hemlev</th>
              <th style={thNumStyle}>Nätter</th>
              <th style={thNumStyle}>Per 100</th>
              <th style={thNumStyle}>Nätter övriga</th>
              <th style={thNumStyle}>Andel av alla nätter</th>
            </tr>
          </thead>
          <tbody>
            {counties.map((row) => (
              <Fragment key={row.countyName}>
                <tr
                  style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}
                  onClick={() => expanded.toggle(row.countyName)}
                >
                  <td style={tdStyle}>
                    {expanded.isOpen(row.countyName) ? '▾' : '▸'}{' '}
                    {row.countyName}
                  </td>
                  <td style={tdNumStyle}>
                    {nf(row.purchaseAndHomeDeliveryCases)}
                  </td>
                  <td style={tdNumStyle}>
                    {nf(row.purchaseAndHomeDeliveryNights)}
                  </td>
                  <td style={tdNumStyle}>{per100(row.nightsPer100Cases)}</td>
                  <td style={tdNumStyle}>{nf(row.otherNights)}</td>
                  <td style={tdNumStyle}>
                    {pct(row.shareOfAllNightsPercentage)}
                  </td>
                </tr>
                {expanded.isOpen(row.countyName) &&
                  row.byLocality.map((sub) => (
                    <tr
                      key={`${row.countyName}-${sub.locality}`}
                      style={{
                        borderBottom: '1px solid #f3f3f3',
                        color: '#555',
                      }}
                    >
                      <td style={{ ...tdStyle, paddingLeft: '28px' }}>
                        {localityLabel[sub.locality]}
                      </td>
                      <td style={tdNumStyle}>
                        {nf(sub.purchaseAndHomeDeliveryCases)}
                      </td>
                      <td style={tdNumStyle}>
                        {nf(sub.purchaseAndHomeDeliveryNights)}
                      </td>
                      <td style={tdNumStyle}>
                        {per100(sub.nightsPer100Cases)}
                      </td>
                      <td style={tdNumStyle}>–</td>
                      <td style={tdNumStyle}>–</td>
                    </tr>
                  ))}
              </Fragment>
            ))}
            <tr style={totalRowStyle}>
              <td style={tdStyle}>Totalt</td>
              <td style={tdNumStyle}>
                {nf(result?.purchaseAndHomeDeliveryCases ?? 0)}
              </td>
              <td style={tdNumStyle}>
                {nf(result?.purchaseAndHomeDeliveryNights ?? 0)}
              </td>
              <td style={tdNumStyle}>{per100(result?.nightsPer100Cases)}</td>
              <td style={tdNumStyle}>{nf(result?.otherNights ?? 0)}</td>
              <td style={tdNumStyle}>
                {result?.totalNights ? '100,0 %' : '–'}
              </td>
            </tr>
          </tbody>
        </table>

        <table style={{ ...tableStyle, marginTop: '16px' }}>
          <thead>
            <tr>
              <th style={thStyle}>Alla län</th>
              <th style={thNumStyle}>Inköp + hemlev</th>
              <th style={thNumStyle}>Nätter</th>
              <th style={thNumStyle}>Per 100</th>
            </tr>
          </thead>
          <tbody>
            {(result?.byLocality ?? []).map((row) => (
              <tr key={row.locality} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}>{localityLabel[row.locality]}</td>
                <td style={tdNumStyle}>
                  {nf(row.purchaseAndHomeDeliveryCases)}
                </td>
                <td style={tdNumStyle}>
                  {nf(row.purchaseAndHomeDeliveryNights)}
                </td>
                <td style={tdNumStyle}>{per100(row.nightsPer100Cases)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <ControlFigures
          items={[
            `${nf(result?.hotelReceipts ?? 0)} hotellkvitton`,
            `${nf(result?.plannedNightsWithoutReceipt ?? 0)} planerade hotellnätter utan kvitto räknas inte`,
          ]}
        />
      </WidgetState>
    </Widget>
  );
};

export default HotelNightsWidget;
